import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  AccessTime as AccessTimeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { fetchTemplateById } from '../redux/slices/templateSlice';
import { clearCurrentDocument } from '../redux/slices/documentSlice';
import { useHasPermission } from '../hooks/usePermission';
import ForbiddenPage from './ForbiddenPage';

/**
 * DocumentTemplateInfoPage - Template information page before document creation
 * 
 * Features:
 * - Template details (title, description, thumbnail, metadata)
 * - Question count, estimated time
 * - "Belge Oluştur" button (navigates to wizard)
 * - Permission: documents.create
 * 
 * Phase 6C: Document Management
 */
const DocumentTemplateInfoPage = () => {
  const { templateId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { currentTemplate: template, loading, error } = useSelector((state) => state.templates);

  // Permission: documents.create
  const hasPermission = useHasPermission('documents.create');

  // Fetch template on mount
  useEffect(() => {
    if (templateId && hasPermission) {
      dispatch(fetchTemplateById(templateId));
    }
  }, [templateId, dispatch, hasPermission]);

  // Handle back
  const handleBack = () => {
    history.goBack();
  };

  // Handle create document (navigate to wizard)
  const handleCreateDocument = () => {
    // Önceki belgeyi temizle
    dispatch(clearCurrentDocument());
    history.push(`/documents/wizard/${templateId}`);
  };

  // Permission-based guard check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'Şablon yüklenirken bir hata oluştu'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Geri Dön
        </Button>
      </Container>
    );
  }

  // No template
  if (!template) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Şablon bulunamadı
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Geri Dön
        </Button>
      </Container>
    );
  }

  // Calculate question count
  const questionCount = template.questions?.length || template.questionTree?.length || 0;
  const estimatedTime = Math.ceil(questionCount * 0.5); // ~30 saniye per question

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Main Card with Split Layout */}
      <Paper
        sx={{
          p: 4,
          '&:hover': {
            boxShadow: 4,
          },
          transition: 'box-shadow 0.3s',
        }}
      >
        <Grid container spacing={4}>
          {/* Left: Template Info (60% width) */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* Header Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Chip
                    label={template.category || 'Diğer'}
                    color="primary"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                    {template.title}
                  </Typography>
                  
                  {/* Metadata - Title'ın altında */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QuestionAnswerIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Soru Sayısı:</strong> {questionCount}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Tahmini Süre:</strong> {estimatedTime} dakika
                      </Typography>
                    </Box>
                    {template.createdBy && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Oluşturan:</strong> {template.createdBy.firstName} {template.createdBy.lastName}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Description - Metadata'dan sonra */}
                  {template.description && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                      {template.description}
                    </Typography>
                  )}

                  {/* Additional Info Section */}
                  <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 2 }}>
                      Bu Şablon Hakkında
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Kategori
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bu şablon <strong>{template.category || 'Diğer'}</strong> kategorisinde yer almaktadır.
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Nasıl Kullanılır?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bu şablonu kullanarak <strong>{questionCount} soruya</strong> cevap vererek belgenizi oluşturabilirsiniz. 
                          Tahmini süre <strong>{estimatedTime} dakika</strong>dır.
                        </Typography>
                      </Box>
                      {template.usageCount !== undefined && template.usageCount > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="text.primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Popülerlik
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Bu şablon <strong>{template.usageCount}</strong> kez kullanılmıştır.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Create Document Button - Top Right */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleCreateDocument}
                  sx={{ ml: 2, minWidth: '180px', alignSelf: 'flex-start' }}
                >
                  Belge Oluştur
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right: A4 Preview (40% width) - Larger */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                position: 'sticky',
                top: 20,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              {/* A4 Ratio Container - Larger */}
              <Paper
                elevation={3}
                sx={{
                  width: '100%',
                  maxWidth: '320px', // Larger preview
                  aspectRatio: '210 / 297', // A4 ratio
                  bgcolor: 'white',
                  p: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.3s',
                }}
                onClick={handleCreateDocument}
              >
                {/* A4 Content Area */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '3px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '2px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: template.contentHtml || '' }}
                    sx={{
                      fontSize: '0.7rem', // Slightly larger for better readability
                      lineHeight: 1.5,
                      color: 'text.primary',
                      '& p': {
                        marginBottom: '0.5em',
                        lineHeight: 1.5,
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginTop: '0.5em',
                        marginBottom: '0.25em',
                        fontSize: 'inherit',
                      },
                      '& h1': { fontSize: '1.3em' },
                      '& h2': { fontSize: '1.15em' },
                      '& h3': { fontSize: '1.05em' },
                      '& ul, & ol': {
                        paddingLeft: '1em',
                        marginBottom: '0.5em',
                      },
                      '& li': {
                        marginBottom: '0.25em',
                      },
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                      },
                      '& table': {
                        fontSize: 'inherit',
                        width: '100%',
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default DocumentTemplateInfoPage;

